import { Logger } from 'aws-amplify';
import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { FlagsContext } from 'src/context';
import { RootState } from 'src/store';
import * as ReduxType from 'src/store/reduxTypes';
import { removeDuplicates } from 'src/utils/array';
import { isUnreadNotification } from 'src/utils/NotificationsUtils';

const logger = new Logger('FilesNotificationsBadge');

export const useFilesNotificationCount = (): number => {
  const { fileNotifications, activeCompanyId } = useSelector(
    (state: ReduxType.RootState) => ({
      fileNotifications: state.notifications.fileNotifications,
      activeCompanyId: state.activeCompany.activeCompanyId,
    }),
    shallowEqual,
  );
  const { EnableManyCompanies } = React.useContext(FlagsContext);

  const esigRequestNotifications = useSelector(
    (state: RootState) => state.notifications.esigRequestNotifications,
  );

  const esigRequestCompleteNotifications = useSelector(
    (state: RootState) => state.notifications.esigRequestCompleteNotifications,
  );

  const [filesNotificationsCounter, setFilesNotificationsCounter] =
    React.useState(0);

  /**
   * This method calculates for each notification
   * group its total unread notification, since files
   * and e-sign unread notifications are calculated differently.
   * Files unread notifications count is the sum of file-upload notified channel.
   * E-sign notifications count is the sum of esign-requests/signature complete actions
   * received at channel.
   */
  const calculateFilesNotificationsCounter = () => {
    logger.debug('Counting primary sidebar file notifications');
    logger.debug('Total file notifications:', fileNotifications.length);
    const unreadFileUploadNotifications = fileNotifications.filter((notif) =>
      EnableManyCompanies
        ? isUnreadNotification(notif.ownerId) &&
          notif.fields.companyId === activeCompanyId
        : isUnreadNotification(notif.ownerId),
    );
    logger.debug('Unread notifications:', unreadFileUploadNotifications.length);
    const fileUploadNotifiedChannelsIds = removeDuplicates(
      unreadFileUploadNotifications.map((notif) => notif.fields.channelId),
    );
    logger.debug(
      'Unique Channel Ids without file upload notifications:',
      fileUploadNotifiedChannelsIds,
    );

    const unreadEsigRequestCompleteNotifications =
      esigRequestCompleteNotifications.filter((notif) =>
        isUnreadNotification(notif.ownerId),
      );

    const totalFileUploadNotificationsCount =
      fileUploadNotifiedChannelsIds.length;
    // If contracts are enabled then esign request notification is to be shown with contracts and not files
    const totalEsignRequestNotificationsCount = 0;
    const totalEsignRequestCompleteNotificationsCount =
      unreadEsigRequestCompleteNotifications.length;

    const totalFilesNotificationsCount =
      totalFileUploadNotificationsCount +
      totalEsignRequestNotificationsCount +
      totalEsignRequestCompleteNotificationsCount;

    setFilesNotificationsCounter(totalFilesNotificationsCount);
  };
  React.useEffect(() => {
    calculateFilesNotificationsCounter();
  }, [
    fileNotifications,
    esigRequestNotifications,
    esigRequestCompleteNotifications,
  ]);

  return filesNotificationsCounter;
};
